interface Point {
  x: number;
  y: number;
}
interface Pose extends Point {
  radius: number;
}
interface PhoneFrame {
  position: number;
  poses: Pose[];
}
interface Body extends Point {
  element: HTMLElement;
  face: HTMLElement;
  radius: number;
  baseRadius: number;
  introRadius: number;
  targetRadius: number;
  yield: number;
  yieldAt?: number;
  phoneBelow?: boolean;
  start: Point;
  end: Point;
  detour?: Point;
  contactDetour?: Point;
  contactEnd?: Point;
  vx: number;
  vy: number;
}
interface Obstacle {
  left: number;
  right: number;
  top: number;
  bottom: number;
}
interface PanelGeometry {
  rectangles: Array<Obstacle & { shift: number }>;
  facts: boolean;
  intro: boolean;
}
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const smooth = (value: number) => {
  const t = clamp(value, 0, 1);
  return t * t * (3 - 2 * t);
};
const PHONE_SAMPLES_PER_STEP = 96;

/** A sleeping spring field: area-weighted contacts, fixed walls and kinematic text panels. */
export class CompanyPeopleHeroBubbles {
  private bodies: Body[] = [];
  private width = 0;
  private height = 0;
  private progress = 0;
  private position = 0;
  private transit = 0;
  private transitScale = 1;
  private frame = 0;
  private lastTime = 0;
  private settlingSince = 0;
  private animated = false;
  private gap = 12;
  private depthCenterY = 0;
  private compact = false;
  private phone = false;
  private endScale = 1;
  private factsBottom = 0;
  private factsTop = 0;
  private storyOffset = 0;
  private viewportHeight = 0;
  private factsOpen = 1;
  private obstacleGeometry?: { panels: PanelGeometry[]; cue: Obstacle[]; approach: number };
  private obstacleCache?: { position: number; rectangles: Obstacle[] };
  private phoneFrames: PhoneFrame[] = [];

  constructor(private root: HTMLElement) {}

  measure(animated: boolean, storyOffset = 0) {
    this.stop();
    if (this.phoneFrames.length)
      for (const body of this.bodies) body.element.style.removeProperty('transform');
    this.phoneFrames = [];
    this.obstacleCache = undefined;
    this.obstacleGeometry = undefined;
    this.storyOffset = storyOffset;
    this.root.classList.remove('has-people-layout');
    this.bodies = [];
    const field = this.root.querySelector<HTMLElement>('.vp-people-bubbles');
    if (!field || !field.offsetHeight) return;
    const compact = window.innerWidth <= 1024;
    this.compact = compact;
    this.phone = window.innerWidth <= 620;
    this.animated = animated;
    this.endScale = 1;
    this.factsBottom = 0;
    this.factsTop = 0;
    this.width = field.clientWidth;
    this.height = field.clientHeight;
    this.gap = compact && animated ? 7 : clamp(this.width * 0.008, 10, 24);
    const bounds = field.getBoundingClientRect();
    const stageTop = this.root.querySelector('.vp-people-stage')?.getBoundingClientRect().top ?? bounds.top;
    this.depthCenterY =
      window.innerWidth <= 1024 ? this.height / 2 : window.innerHeight / 2 - (bounds.top - stageTop);
    const facts = this.root.querySelector<HTMLElement>('.vp-people-facts-content');
    for (const element of this.root.querySelectorAll<HTMLElement>('.vp-people-bubble')) {
      if (!element.offsetWidth || !element.offsetHeight) continue;
      const rect = element.getBoundingClientRect();
      const start = { x: rect.left + rect.width / 2 - bounds.left, y: rect.top + rect.height / 2 - bounds.top };
      const baseRadius = rect.width / 2;
      const introRadius = baseRadius * (this.phone && element.dataset.phoneFeatured === 'true' ? 2.2 : 1);
      this.bodies.push({
        element,
        face: element.querySelector<HTMLElement>('.vp-people-face') ?? element,
        ...start,
        start,
        end: { ...start },
        radius: introRadius,
        baseRadius,
        introRadius,
        targetRadius: introRadius,
        yield: 0,
        vx: 0,
        vy: 0,
      });
    }
    // Establish a collision-free first frame before enabling transforms.
    this.root.classList.add('has-people-layout');
    this.progress = 0;
    this.position = 0;
    this.transit = 0;
    this.transitScale = 1;
    if (this.phone) this.placePhoneIntro(bounds);
    this.constrain(this.obstacles(true), compact ? 112 : 48);
    for (const body of this.bodies) body.start = { x: body.x, y: body.y };
    if (compact && !animated) {
      this.paint();
      return;
    }

    // Keep the loose portrait constellation. Expand it around the settled facts,
    // including their top and bottom edges, instead of filling uniform side rails.
    const factsPanel = facts?.closest<HTMLElement>('[data-story-panel]');
    const stage = this.root.querySelector('.vp-people-stage');
    if (facts && factsPanel && stage) {
      const rect = facts.getBoundingClientRect();
      const inset = bounds.top - stage.getBoundingClientRect().top;
      const top = rect.top - factsPanel.getBoundingClientRect().top - inset;
      const restingFacts = {
        left: rect.left - bounds.left,
        right: rect.right - bounds.left,
        top,
        bottom: top + rect.height,
      };
      const gutter = Math.min(restingFacts.left, this.width - restingFacts.right);
      this.factsBottom = restingFacts.bottom;
      this.factsTop = restingFacts.top;
      const diameter = Math.max(...this.bodies.map(body => body.baseRadius * 2), 1);
      const approach = parseFloat(getComputedStyle(this.root).getPropertyValue('--people-facts-approach')) || 0;
      if (this.phone) {
        const room = Math.min(restingFacts.top, this.height - restingFacts.bottom) - this.gap - 8;
        this.endScale = clamp(room / diameter, 0.5, 1);
      } else if (compact) this.endScale = clamp((gutter - this.gap * 2 - 4) / diameter, 0.4, 0.85);
      this.bodies.forEach((body, index) => {
        body.radius = body.baseRadius * this.endScale;
        if (this.phone) {
          const half = Math.ceil(this.bodies.length / 2);
          const above = index < half;
          body.phoneBelow = !above;
          body.yieldAt = clamp(1 - (body.start.y + body.baseRadius + 36 - restingFacts.top + approach) /
            (this.height + 112), 0.12, 0.8);
          const column = index % half;
          const slots = above
            ? [[0.08, 0.74], [0.28, 0.12], [0.51, 0.56], [0.7, 0.06], [0.92, 0.88]]
            : this.endScale < 0.7
              ? [[0.06, 0.72], [0.2, 0.18], [0.35, 0.04], [0.71, 0.82], [0.94, 0.42]]
              : [[0.09, 0.72], [0.3, 0.18], [0.52, 0.04], [0.73, 0.82], [0.92, 0.42]];
          const slot = slots[column % slots.length]!;
          const minY = above ? body.radius + 4 : restingFacts.bottom + body.radius + this.gap;
          const maxY = above ? restingFacts.top - body.radius - this.gap : this.height - body.radius - 16;
          body.x = this.width * slot[0]!;
          body.y = minY + Math.max(0, maxY - minY) * slot[1]!;
          return;
        }
        const angle = index * 2.399963;
        const spread = 1.04 + (Math.sin(angle * 1.7) + 1) * 0.04;
        const origin = body.start;
        body.x = this.width / 2 + (origin.x - this.width / 2) * spread + Math.sin(angle) * this.width * 0.014;
        body.y = this.height / 2 + (origin.y - this.height / 2) * spread + Math.cos(angle) * this.height * 0.025;
      });
      this.constrain([restingFacts, ...this.cueObstacle()], 80);
      for (const body of this.bodies) {
        body.end = { x: body.x, y: body.y };
        body.x = body.start.x;
        body.y = body.start.y;
        body.radius = body.introRadius;
      }
      // On narrow desktops the circles yield slightly into depth while passing
      // the cards. Both settled compositions retain their full portrait sizes.
      this.transitScale = this.phone ? 1 : compact ? 0.52 : clamp((gutter - this.gap * 2 - 4) / (diameter * 2), 0.68, 1);
      this.planPassage(restingFacts, false);
      this.planPassage(restingFacts, true);
      if (compact) {
        const contact = this.root.querySelector<HTMLElement>('.vp-people-contact-content');
        const panel = contact?.closest<HTMLElement>('[data-story-panel]');
        if (contact && panel) {
          const contactBounds = contact.getBoundingClientRect();
          const top = contactBounds.top - panel.getBoundingClientRect().top - inset;
          for (const [index, body] of this.bodies.entries()) {
            body.radius = body.baseRadius * this.endScale;
            if (this.phone) {
              const half = Math.ceil(this.bodies.length / 2);
              const above = index < half;
              const slots = above
                ? [[0.08, 0.55], [0.3, 0.1], [0.52, 0.9], [0.73, 0.25], [0.92, 0.7]]
                : [[0.09, 0.2], [0.3, 0.75], [0.52, 0.4], [0.73, 0.9], [0.92, 0.12]];
              const slot = slots[index % half % slots.length]!;
              const minY = above ? body.radius + 4 : top + contactBounds.height + body.radius + this.gap;
              const maxY = above ? top - body.radius - this.gap : this.height - body.radius - 16;
              body.x = this.width * slot[0]!;
              body.y = minY + Math.max(0, maxY - minY) * slot[1]!;
            } else {
              body.x = body.start.x;
              body.y = body.start.y;
            }
          }
          this.constrain([{ left: contactBounds.left - bounds.left, right: contactBounds.right - bounds.left, top, bottom: top + contactBounds.height }], 112);
          for (const body of this.bodies) {
            body.contactEnd = { x: body.x, y: body.y };
            body.x = body.start.x;
            body.y = body.start.y;
            body.radius = body.introRadius;
          }
        }
      }
    }
    if (animated) this.measureObstacles();
    if (animated && this.phone) this.preparePhoneMotion();
    this.paint();
  }

  /** Bake the measured composition once. Scrolling only replays this small table. */
  private preparePhoneMotion() {
    const initialFactsOpen = this.factsOpen;
    const half = Math.ceil(this.bodies.length / 2);
    const origins = [...this.bodies].sort((a, b) => a.start.y - b.start.y);
    const destinations = this.bodies.map(body => ({ end: body.end, contactEnd: body.contactEnd }))
      .sort((a, b) => a.end.y - b.end.y);
    // Preserve each row's left-to-right order while it opens around the copy.
    for (let group = 0; group < 2; group++) {
      const row = origins.slice(group * half, (group + 1) * half).sort((a, b) => a.start.x - b.start.x);
      const slots = destinations.slice(group * half, (group + 1) * half).sort((a, b) => a.end.x - b.end.x);
      row.forEach((body, index) => {
        body.end = slots[index]!.end;
        body.contactEnd = slots[index]!.contactEnd;
        body.phoneBelow = group === 1;
        const x = body.end.x < this.width / 2 ? -body.baseRadius * 0.45 : this.width + body.baseRadius * 0.45;
        body.detour = { x, y: body.start.y };
        body.contactDetour = { x, y: body.end.y };
      });
    }
    for (let index = 0; index <= PHONE_SAMPLES_PER_STEP * 2; index++) {
      const position = index / PHONE_SAMPLES_PER_STEP;
      const contact = position > 1;
      const phase = contact ? position - 1 : position;
      this.position = position;
      this.factsOpen = clamp((position - 0.35) / 0.65, 0, 1);
      for (const body of this.bodies) {
        const from = contact ? body.end : body.start;
        const to = contact ? body.contactEnd ?? body.end : body.end;
        const progress = contact ? smooth(phase) : smooth((phase - 0.25) / 0.75);
        let x = from.x + (to.x - from.x) * progress;
        const y = from.y + (to.y - from.y) * progress;
        const radius = body.baseRadius + (body.introRadius - body.baseRadius) * (1 - smooth(position / 0.3));
        body.radius = radius * (1 + (this.endScale - 1) * smooth(position));
        body.yield = this.phoneYield(body, position);
        const detour = contact ? body.contactDetour : body.detour;
        if (detour) x += (detour.x - x) * body.yield;
        // Continue from the preceding pose so neighbours keep their ordering
        // instead of finding unrelated collision-free arrangements at each key.
        body.x += (x - body.x) * 0.45;
        body.y += (y - body.y) * 0.45;
        if (phase === 0 || phase === 1) {
          body.x = x;
          body.y = y;
        }
        body.vx = body.vy = 0;
      }
      // This bounded planning pass never runs from a scroll or animation callback.
      this.constrain(this.obstacles(), 48, true);
      this.phoneFrames.push({ position, poses: this.capturePhonePose() });
    }
    this.refinePhoneMotion();
    this.position = 0;
    this.factsOpen = initialFactsOpen;
    this.obstacleCache = undefined;
    this.replayPhoneMotion(0);
  }

  private replayPhoneMotion(position: number) {
    let low = 0;
    let high = this.phoneFrames.length - 1;
    while (high - low > 1) {
      const middle = (low + high) >>> 1;
      if (this.phoneFrames[middle]!.position <= position) low = middle;
      else high = middle;
    }
    this.interpolatePhonePose(this.phoneFrames[low]!, this.phoneFrames[high]!, position);
  }

  private capturePhonePose(): Pose[] {
    return this.bodies.map(body => ({ x: body.x, y: body.y, radius: body.radius }));
  }

  private phoneYield(body: Body, position: number) {
    if (position > 1) return smooth((2 - position) / 0.18);
    if (!body.phoneBelow) return 0;
    const approach = this.obstacleGeometry?.approach ?? 0;
    const factsOpen = clamp((position - 0.35) / 0.65, 0, 1);
    const bottom = this.factsBottom + (1 - position) * this.viewportHeight + approach * (factsOpen - 1);
    const room = this.height - 16 - bottom - body.radius * 2 - this.gap;
    return smooth((position - (body.yieldAt ?? 0.35)) / 0.12) *
      (1 - smooth(room / (body.radius * 2 + this.gap)));
  }

  private interpolatePhonePose(from: PhoneFrame, to: PhoneFrame, position: number) {
    const progress = clamp((position - from.position) / (to.position - from.position), 0, 1);
    this.bodies.forEach((body, bodyIndex) => {
      const a = from.poses[bodyIndex]!;
      const b = to.poses[bodyIndex]!;
      body.x = a.x + (b.x - a.x) * progress;
      body.y = a.y + (b.y - a.y) * progress;
      body.radius = a.radius + (b.radius - a.radius) * progress;
    });
  }

  private refinePhoneMotion() {
    // Check the paths between keys too. A valid pair of poses can otherwise
    // exchange neighbours or cut across a moving card during interpolation.
    const frames = this.phoneFrames;
    const refined = [frames[0]!];
    const valid = (from: PhoneFrame, to: PhoneFrame, fraction: number) => {
      this.position = from.position + (to.position - from.position) * fraction;
      this.factsOpen = clamp((this.position - 0.35) / 0.65, 0, 1);
      this.interpolatePhonePose(from, to, this.position);
      const obstacles = this.obstacles();
      return this.bodies.every((body, index) =>
        this.bodies.slice(index + 1).every(other =>
          Math.hypot(body.x - other.x, body.y - other.y) >= body.radius + other.radius + 5,
        ) && obstacles.every(rect =>
          Math.hypot(body.x - clamp(body.x, rect.left, rect.right), body.y - clamp(body.y, rect.top, rect.bottom)) >=
            body.radius + 2,
        ),
      );
    };
    const append = (from: PhoneFrame, to: PhoneFrame, depth: number) => {
      if (depth < 8 && ![0.25, 0.5, 0.75].every(fraction => valid(from, to, fraction))) {
        this.position = (from.position + to.position) / 2;
        this.factsOpen = clamp((this.position - 0.35) / 0.65, 0, 1);
        this.interpolatePhonePose(from, to, this.position);
        for (const body of this.bodies) body.yield = this.phoneYield(body, this.position);
        this.constrain(this.obstacles(), 48, true);
        const middle = { position: this.position, poses: this.capturePhonePose() };
        append(from, middle, depth + 1);
        append(middle, to, depth + 1);
      } else refined.push(to);
    };
    for (let index = 1; index < frames.length; index++) append(frames[index - 1]!, frames[index]!, 0);
    this.phoneFrames = refined;
  }

  private placePhoneIntro(field: DOMRect) {
    const content = this.root.querySelector<HTMLElement>('.vp-people-copy .vp-people-panel-content');
    if (!content?.offsetHeight) return;
    const copy = content.getBoundingClientRect();
    const bottom = copy.bottom - field.top - this.storyOffset;
    const slots = [[0.09, 0.09], [0.42, 0.03], [0.8, 0.12], [0.94, 0.45], [0.58, 0.39],
      [0.22, 0.45], [0.08, 0.78], [0.4, 0.9], [0.72, 0.76], [0.94, 0.94]];
    this.bodies.forEach((body, index) => {
      const slot = slots[index % slots.length]!;
      const space = Math.max(0, this.height - bottom - body.radius * 2 - 64);
      body.x = this.width * slot[0]!;
      body.y = bottom + body.radius + 32 + space * slot[1]!;
    });
    // A short screen leaves a shallow field below the CTA. Start the large
    // portrait at its side so the solver can spread its neighbours freely.
    const featured = this.bodies.find(body => body.introRadius > body.baseRadius);
    if (featured && this.height - bottom < featured.radius * 4) {
      featured.x = featured.radius + 8;
      featured.y = this.height - featured.radius - 16;
    }
  }

  private planPassage(facts: Obstacle, contact: boolean) {
    const scale = this.compact ? this.endScale * this.transitScale : contact ? this.transitScale : 1;
    for (const right of [false, true]) {
      const side = this.bodies
        .filter(
          body =>
            body.end.x >= this.width / 2 === right &&
            (contact || (this.phone ? body.phoneBelow : body.end.y + body.baseRadius + this.gap > facts.top)),
        )
        .sort((a, b) => a.start.y - b.start.y);
      const gutter = (right ? this.width - facts.right : facts.left) - this.gap * 2;
      const columns = gutter >= 190 ? 2 : 1;
      for (let column = 0; column < columns; column++) {
        const lane = side.filter((_, index) => index % columns === column);
        const slack = Math.max(
          0,
          this.height -
            32 -
            lane.reduce((sum, body) => sum + body.baseRadius * scale * 2, 0) -
            this.gap * (lane.length - 1),
        );
        const weights = Array.from(
          { length: lane.length + 1 },
          (_, index) => 1.4 + Math.sin(index * 2.1 + column) * 0.6,
        );
        const total = weights.reduce((sum, weight) => sum + weight, 0);
        let y = 16 + (slack * weights[0]!) / total;
        lane.forEach((body, index) => {
          const radius = body.baseRadius * scale;
          const freeWidth = Math.max(0, gutter / columns - radius * 2 - this.gap);
          const x =
            this.gap * 1.5 +
            (column * gutter) / columns +
            radius +
            (Math.sin(index * 2.4 + column) + 1) * 0.5 * freeWidth;
          const target = { x: right ? this.width - x : x, y: y + radius };
          if (this.phone) {
            target.x = right ? this.width + radius * 0.45 : -radius * 0.45;
            if (!contact) target.y = body.start.y;
          }
          if (contact) body.contactDetour = target;
          else body.detour = target;
          y += radius * 2 + this.gap + (slack * weights[index + 1]!) / total;
        });
      }
    }
  }

  update(position: number, factsOpen = 1) {
    if (!this.bodies.length) return;
    const next = this.animated ? Math.max(0, position) : 0;
    if (Math.abs(this.position - next) < 0.00001) return;
    if (this.phoneFrames.length) {
      this.position = next;
      this.replayPhoneMotion(next);
      this.paint();
      return;
    }
    const jump = this.phone && Math.abs(this.position - next) > 0.65 && Math.abs(next - Math.round(next)) < 0.001;
    const returningToFacts = Math.abs(next - 1) < 0.0001 && this.position > 1;
    this.position = next;
    this.factsOpen = factsOpen;
    this.progress = clamp(next, 0, 1);
    const phase = next <= 1 ? next : clamp(next - 1, 0, 1);
    const ease = (value: number) => {
      const t = clamp(value, 0, 1);
      return t * t * (3 - 2 * t);
    };
    this.transit = this.phone ? 0 : ease(phase / (this.compact ? 0.09 : 0.22)) * ease((1 - phase) / 0.045);
    this.settlingSince = performance.now();
    // Scroll and springs share the same painted frame, even for an instant keyboard jump.
    const restingScale = 1 + (this.endScale - 1) * ease(this.progress);
    // Restore the regular radius before the cards start displacing the field.
    const introOpen = 1 - ease(this.progress / 0.3);
    for (const body of this.bodies) {
      body.yield = this.phone
        ? next > 1 ? ease((1 - phase) / 0.015)
          : body.phoneBelow ? ease((next - (body.yieldAt ?? 0.35)) / 0.12) : returningToFacts ? 1 : 0
        : this.transit;
      const radius = body.baseRadius + (body.introRadius - body.baseRadius) * introOpen;
      body.targetRadius = radius * restingScale * (1 - (1 - this.transitScale) * body.yield);
      // Yield immediately; recover depth only after there is room around returning copy.
      body.radius = this.compact ? Math.min(body.radius, body.targetRadius) : body.targetRadius;
      if (jump) {
        // Direct links and restored scroll positions have no intervening card sweep.
        const pose = next < 0.5 ? body.start : next < 1.5 ? body.end : body.contactEnd ?? body.end;
        body.x = pose.x;
        body.y = pose.y;
        body.radius = body.targetRadius;
        body.yield = 0;
        body.vx = body.vy = 0;
      }
    }
    this.constrain(this.obstacles(), this.compact ? 112 : 64);
    this.paint();
    if (!this.frame) {
      this.lastTime = performance.now();
      this.frame = requestAnimationFrame(this.tick);
    }
  }

  disconnect() {
    this.stop();
    if (this.phoneFrames.length)
      for (const body of this.bodies) body.element.style.removeProperty('transform');
    this.phoneFrames = [];
    this.bodies = [];
  }

  private stop() {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    if (this.root.dataset.bubblesMoving !== 'false') this.root.dataset.bubblesMoving = 'false';
  }

  private obstacles(initial = false): Obstacle[] {
    // Spring settling does not move the text. Reuse its geometry until the next
    // scroll position or remeasurement instead of forcing style/layout per tick.
    if (!initial && this.obstacleCache?.position === this.position) return this.obstacleCache.rectangles;
    if (!initial && this.obstacleGeometry) {
      const { panels, cue, approach } = this.obstacleGeometry;
      const offset = -this.position * this.viewportHeight;
      const rectangles: Obstacle[] = [];
      for (const panel of panels) {
        const translation = offset + (panel.facts ? approach * (this.factsOpen - 1) : 0);
        let left = Infinity;
        let right = -Infinity;
        let top = Infinity;
        let bottom = -Infinity;
        for (const rect of panel.rectangles) {
          const y = translation + rect.shift * (1 - this.factsOpen);
          left = Math.min(left, rect.left);
          right = Math.max(right, rect.right);
          top = Math.min(top, rect.top + y);
          bottom = Math.max(bottom, rect.bottom + y);
        }
        if (bottom > 0 && top < this.height) rectangles.push({ left, right, top, bottom });
        if (this.phone && panel.intro && bottom + 16 > 0)
          rectangles.push({ left: 0, right: this.width, top: 0, bottom: bottom + 16 });
      }
      rectangles.push(...cue);
      this.obstacleCache = { position: this.position, rectangles };
      return rectangles;
    }
    const field = this.root.querySelector('.vp-people-bubbles')!.getBoundingClientRect();
    const trackOffset = initial && this.root.classList.contains('has-people-pin') ? this.storyOffset : 0;
    const panels = [...this.root.querySelectorAll<HTMLElement>('.vp-people-panel-content')]
      .filter(element => element.offsetHeight > 0 && (!initial || element.closest('[data-story-panel="intro"]')))
      .map(element => {
        const rectangles = [element.getBoundingClientRect()];
        if (element.classList.contains('vp-people-facts-content'))
          for (const card of element.querySelectorAll<HTMLElement>('.vp-people-fact'))
            if (card.offsetHeight) rectangles.push(card.getBoundingClientRect());
        return {
          left: Math.min(...rectangles.map(rect => rect.left)) - field.left,
          right: Math.max(...rectangles.map(rect => rect.right)) - field.left,
          top: Math.min(...rectangles.map(rect => rect.top)) - field.top - trackOffset,
          bottom: Math.max(...rectangles.map(rect => rect.bottom)) - field.top - trackOffset,
        };
      })
      .filter(rect => rect.bottom > 0 && rect.top < this.height);
    if (this.phone) {
      const intro = this.root.querySelector('.vp-people-copy .vp-people-panel-content')?.getBoundingClientRect();
      const bottom = intro ? intro.bottom - field.top - trackOffset + 16 : 0;
      if (bottom > 0) panels.push({ left: 0, right: this.width, top: 0, bottom });
    }
    const rectangles = [...panels, ...this.cueObstacle()];
    if (!initial) this.obstacleCache = { position: this.position, rectangles };
    return rectangles;
  }

  private measureObstacles() {
    // Measure the immutable panel/card geometry once. Their existing CSS motion
    // is a translation, so collision bounds can follow it without layout reads.
    const field = this.root.querySelector('.vp-people-bubbles')!.getBoundingClientRect();
    this.viewportHeight = this.root.querySelector<HTMLElement>('.vp-people-stage')!.clientHeight;
    const approach = this.phone
      ? parseFloat(getComputedStyle(this.root).getPropertyValue('--people-facts-approach')) || 0
      : 0;
    const panels: PanelGeometry[] = [];
    for (const element of this.root.querySelectorAll<HTMLElement>('.vp-people-panel-content')) {
      if (!element.offsetHeight) continue;
      const facts = element.classList.contains('vp-people-facts-content');
      const panel = element.closest<HTMLElement>('[data-story-panel]')!;
      const open = parseFloat(panel.style.getPropertyValue('--people-facts-open'));
      const factsOpen = Number.isFinite(open) ? open : 1;
      const translation = this.storyOffset + (facts ? approach * (factsOpen - 1) : 0);
      const read = (node: HTMLElement, shift = 0) => {
        const rect = node.getBoundingClientRect();
        const y = translation + shift * (1 - factsOpen);
        return {
          left: rect.left - field.left,
          right: rect.right - field.left,
          top: rect.top - field.top - y,
          bottom: rect.bottom - field.top - y,
          shift,
        };
      };
      const rectangles = [read(element)];
      if (facts)
        for (const card of element.querySelectorAll<HTMLElement>('.vp-people-fact')) {
          if (!card.offsetHeight) continue;
          const shift = card.style.transform ? 0 : parseFloat(getComputedStyle(card).getPropertyValue('--fact-shift')) || 0;
          rectangles.push(read(card, shift));
        }
      panels.push({ rectangles, facts, intro: panel.dataset.storyPanel === 'intro' });
    }
    this.obstacleGeometry = { panels, cue: this.cueObstacle(), approach };
  }

  private cueObstacle(): Obstacle[] {
    const cue = this.root.querySelector<HTMLElement>('.vp-people-scroll');
    if (!cue) return [];
    const field = this.root.querySelector('.vp-people-bubbles')!.getBoundingClientRect();
    const rect = cue.getBoundingClientRect();
    return [
      {
        left: rect.left - field.left,
        right: rect.right - field.left,
        top: rect.top - field.top,
        bottom: rect.bottom - field.top,
      },
    ];
  }

  private tick = (time: number) => {
    const dt = clamp((time - this.lastTime) / 1000, 1 / 240, 1 / 10);
    this.lastTime = time;
    const obstacles = this.obstacles();
    // Smaller bubbles respond faster; large portraits carry more inertia.
    for (const body of this.bodies) {
      if (this.phone && Math.abs(this.position - 1) < 0.0001 && body.yield > 0 &&
        (body.phoneBelow ? body.y >= this.factsBottom + 4.5 : body.y + body.radius <= this.factsTop - 4.5)) {
        // Once the centre rounds the lower corner, collision projection keeps
        // the radius clear as it returns. Waiting for full clearance can wedge
        // a portrait between the card corner and an already settled neighbour.
        body.yield *= Math.exp(-18 * dt);
        if (body.yield < 0.003) body.yield = 0;
      }
      if (this.compact) {
        body.radius += (body.targetRadius - body.radius) * (1 - Math.exp(-12 * dt));
        if (Math.abs(body.targetRadius - body.radius) < 0.01) body.radius = body.targetRadius;
      }
      const detour = this.position <= 1 ? body.detour : body.contactDetour;
      const frequency = (this.phone ? 28 : detour ? 36 : 19) - Math.min(5, body.baseRadius / 24);
      const steps = Math.max(1, Math.ceil(dt * 120));
      const step = dt / steps;
      const contact = this.position > 1 && body.contactEnd;
      const from = contact ? body.end : body.start;
      const to = contact || body.end;
      const progress = contact ? clamp(this.position - 1, 0, 1) :
        this.phone ? clamp((this.progress - 0.35) / 0.65, 0, 1) : this.progress;
      let x = from.x + (to.x - from.x) * progress;
      let y = from.y + (to.y - from.y) * progress;
      if (detour) {
        x += (detour.x - x) * body.yield;
        // Keep phone portraits travelling toward their assigned upper/lower
        // band while they pass the edges, so each band receives half the field.
        if (!this.phone) y += (detour.y - y) * body.yield;
      }
      for (let index = 0; index < steps; index++) {
        body.vx += ((x - body.x) * frequency ** 2 - 1.75 * frequency * body.vx) * step;
        body.vy += ((y - body.y) * frequency ** 2 - 1.75 * frequency * body.vy) * step;
        body.x += body.vx * step;
        body.y += body.vy * step;
      }
    }
    this.constrain(obstacles, this.compact ? 112 : 64);
    this.paint();
    // Stop entirely at rest, including after reverse scrolling or interrupted gestures.
    const moving = this.bodies.some(body => Math.hypot(body.vx, body.vy) > 0.8 || Math.abs(body.radius - body.targetRadius) > 0.01 ||
      (this.phone && Math.abs(this.position - 1) < 0.0001 && body.yield > 0));
    if (moving && time - this.settlingSince < 1600) {
      if (this.root.dataset.bubblesMoving !== 'true') this.root.dataset.bubblesMoving = 'true';
      this.frame = requestAnimationFrame(this.tick);
    } else this.stop();
  };

  private constrain(obstacles: Obstacle[], iterations: number, planning = false) {
    if (this.phone && !planning) iterations = Math.max(iterations, 256);
    for (let iteration = 0; iteration < iterations; iteration++) {
      let correction = 0;
      for (let index = 0; index < this.bodies.length; index++) {
        const a = this.bodies[index]!;
        for (let other = index + 1; other < this.bodies.length; other++) {
          const b = this.bodies[other]!;
          let dx = b.x - a.x;
          const dy = b.y - a.y;
          // A perfectly vertical stack at a side wall has no sideways contact
          // normal. Give it a tiny inward bias so circles can settle into a row.
          if (this.phone && Math.abs(dx) < 0.75) dx = a.x < this.width / 2 ? -0.75 : 0.75;
          const reach = a.radius + b.radius + this.gap;
          const squaredDistance = dx * dx + dy * dy;
          if (squaredDistance >= reach * reach) continue;
          const distance = Math.sqrt(squaredDistance) || 0.001;
          const overlap = reach - distance;
          correction = Math.max(correction, overlap);
          const nx = distance === 0.001 ? 1 : dx / distance;
          const ny = dy / distance;
          const weight = b.radius ** 2 / (a.radius ** 2 + b.radius ** 2);
          a.x -= nx * overlap * weight;
          a.y -= ny * overlap * weight;
          b.x += nx * overlap * (1 - weight);
          b.y += ny * overlap * (1 - weight);
          // Resolve approaching normal velocity with low restitution; no idle bouncing.
          const velocity = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
          if (velocity < 0) {
            a.vx += nx * velocity * 1.12 * weight;
            a.vy += ny * velocity * 1.12 * weight;
            b.vx -= nx * velocity * 1.12 * (1 - weight);
            b.vy -= ny * velocity * 1.12 * (1 - weight);
          }
        }
      }
      for (const body of this.bodies) {
        const beforeX = body.x;
        const beforeY = body.y;
        // On a phone the viewport is a window, not a rigid side wall: circles
        // briefly slip past its edges as the cards push through the field.
        const spill = this.phone ? body.yield * body.radius * 1.65 : 0;
        const minX = body.radius + 2 - spill;
        const maxX = this.width - body.radius - 2 + spill;
        // Contacts may push a circle past a wall. Restore a feasible centre before
        // choosing an escape around text, including on narrow mobile perimeters.
        body.x = clamp(body.x, minX, maxX);
        body.y = clamp(body.y, body.radius + 4, this.height - body.radius - 16);
        for (const rect of obstacles) {
          const reach = body.radius + (this.compact && this.animated ? 4.5 : this.gap);
          const dx = body.x - clamp(body.x, rect.left, rect.right);
          const dy = body.y - clamp(body.y, rect.top, rect.bottom);
          const squaredDistance = dx * dx + dy * dy;
          if (squaredDistance >= reach * reach) continue;
          const distance = Math.sqrt(squaredDistance);
          const nextX = body.x + (dx / (distance || 1)) * (reach - distance);
          const nextY = body.y + (dy / (distance || 1)) * (reach - distance);
          if (
            distance > 0 &&
            nextX >= minX &&
            nextX <= maxX &&
            nextY >= body.radius + 4 &&
            nextY <= this.height - body.radius - 16
          ) {
            body.x = nextX;
            body.y = nextY;
          } else {
            const escapes = [
              { distance: body.x - rect.left + reach, x: rect.left - reach, y: body.y },
              { distance: rect.right - body.x + reach, x: rect.right + reach, y: body.y },
              { distance: body.y - rect.top + reach, x: body.x, y: rect.top - reach },
              { distance: rect.bottom - body.y + reach, x: body.x, y: rect.bottom + reach },
            ]
              .filter(
                point =>
                  point.x >= minX &&
                  point.x <= maxX &&
                  point.y >= body.radius + 4 &&
                  point.y <= this.height - body.radius - 16,
              )
              .sort((a, b) => a.distance - b.distance);
            if (escapes[0]) {
              body.x = escapes[0].x;
              body.y = escapes[0].y;
            }
          }
        }
        body.x = clamp(body.x, minX, maxX);
        body.y = clamp(body.y, body.radius + 4, this.height - body.radius - 16);
        correction = Math.max(correction, Math.abs(body.x - beforeX), Math.abs(body.y - beforeY));
      }
      // Below the painted coordinate precision, further passes do not change
      // the composition. Keep the full iteration budget for difficult contacts.
      if (correction < 0.0001) break;
    }
  }

  private paint() {
    for (const body of this.bodies) {
      if (this.phoneFrames.length) {
        // One compositor transform; no inherited style changes or changing filters.
        body.element.style.transform = `translate3d(${body.x.toFixed(3)}px, ${body.y.toFixed(3)}px, 0) translate(-50%, -50%) scale(${body.radius / body.baseRadius})`;
        continue;
      }
      body.element.style.setProperty('--person-layout-x', `${body.x.toFixed(3)}px`);
      body.element.style.setProperty('--person-layout-y', `${body.y.toFixed(3)}px`);
      body.element.style.setProperty('--person-layout-scale', String(body.radius / body.baseRadius));
      // Enlarging the intro portrait must not also magnify its depth blur.
      this.paintDepth(body.face, body.x, body.y, Math.max(1, body.radius / body.baseRadius));
    }
  }

  private paintDepth(element: HTMLElement, x: number, y: number, scale = 1) {
    const distance = Math.hypot((x - this.width / 2) / (this.width / 2), (y - this.depthCenterY) / (this.height / 2));
    const depth = clamp(1 - distance, 0, 1);
    const falloff = depth * depth * (3 - 2 * depth);
    const amount = clamp(this.width * 0.002, 2, 5.5) * falloff / scale;
    // Quantize tiny changes so moving portraits can reuse their filtered texture.
    const blur = `${(Math.round(amount * 20) / 20).toFixed(2)}px`;
    if (element.style.getPropertyValue('--person-depth-blur') !== blur)
      element.style.setProperty('--person-depth-blur', blur);
    const opacity = (1 - 0.25 * falloff).toFixed(3);
    if (element.style.getPropertyValue('--person-depth-opacity') !== opacity)
      element.style.setProperty('--person-depth-opacity', opacity);
  }
}
